/**
 * OverridesPanel - Configure per-key MIDI overrides
 * Spec §5.7: Key overrides (MIDI actions)
 * Spec §5.8: MIDI action types
 */

import { useState, useEffect } from 'react';
import { useConfigStore } from '@/store/configStore';
import type { KeyAction, ActionType } from '@/types/midi';
import { getNoteName } from '@/lib/noteMapping';

const actionTypes: { value: ActionType; label: string }[] = [
  { value: 'note', label: 'Note' },
  { value: 'cc', label: 'Control Change' },
  { value: 'program_change', label: 'Program Change' },
  { value: 'chord', label: 'Chord' },
  { value: 'layer_toggle', label: 'Layer Toggle' },
  { value: 'layer_momentary', label: 'Layer Momentary' },
];

function createDefaultAction(type: ActionType): KeyAction {
  switch (type) {
    case 'note':
      return { type: 'note', note: 60, channel: 0 };
    case 'cc':
      return { type: 'cc', cc: 1, value: 127, channel: 0 };
    case 'program_change':
      return { type: 'program_change', program: 0, channel: 0 };
    case 'chord':
      return { type: 'chord', notes: [60, 64, 67], channel: 0 };
    case 'layer_toggle':
      return { type: 'layer_toggle', target_layer: 1 };
    case 'layer_momentary':
      return { type: 'layer_momentary', target_layer: 1 };
    case 'bank_program':
      return { type: 'bank_program', bank_msb: 0, bank_lsb: 0, program: 0, channel: 0 };
  }
}

export function OverridesPanel() {
  const selectedKey = useConfigStore((state) => state.selectedKey);
  const activeLayer = useConfigStore((state) => state.activeLayer);
  const overrides = useConfigStore((state) => state.overrides);
  const setSelectedKey = useConfigStore((state) => state.setSelectedKey);

  const addOverride = useConfigStore((state) => state.addOverride);
  const removeOverride = useConfigStore((state) => state.removeOverride);

  // Find existing override for selected key
  const existingOverride = selectedKey
    ? overrides.find(
        (o) => o.pos.q === selectedKey.q && o.pos.r === selectedKey.r && o.layer === activeLayer
      )
    : undefined;

  // Local state for editing
  const [actionType, setActionType] = useState<ActionType>('note');
  const [action, setAction] = useState<KeyAction>(createDefaultAction('note'));

  // Local string state for chord notes input (allows free editing)
  const [chordNotesInput, setChordNotesInput] = useState('');

  // Sync local state with existing override
  useEffect(() => {
    if (existingOverride) {
      setActionType(existingOverride.action.type);
      setAction(existingOverride.action);
      // Sync chord notes input string
      if (existingOverride.action.type === 'chord') {
        setChordNotesInput(existingOverride.action.notes.join(', '));
      }
    } else {
      setActionType('note');
      setAction(createDefaultAction('note'));
      setChordNotesInput('60, 64, 67');
    }
  }, [existingOverride, selectedKey]);

  const handleActionTypeChange = (newType: ActionType) => {
    setActionType(newType);
    const newAction = createDefaultAction(newType);
    setAction(newAction);
    // Reset chord notes input when switching to chord type
    if (newType === 'chord' && newAction.type === 'chord') {
      setChordNotesInput(newAction.notes.join(', '));
    }
  };

  // Parse chord notes string and update action
  const parseChordNotes = () => {
    const notes = chordNotesInput
      .split(',')
      .map((s) => parseInt(s.trim(), 10))
      .filter((n) => !isNaN(n) && n >= 0 && n <= 127)
      .slice(0, 8); // Max 8 notes
    if (notes.length > 0 && action.type === 'chord') {
      setAction({ ...action, notes });
    }
  };

  const handleSave = () => {
    if (!selectedKey) return;
    addOverride(selectedKey, activeLayer, action);
  };

  const handleRemove = () => {
    if (!selectedKey) return;
    removeOverride(selectedKey, activeLayer);
  };

  const handleClearSelection = () => {
    setSelectedKey(null);
  };

  // Render action-specific editor
  const renderActionEditor = () => {
    switch (action.type) {
      case 'note':
        return (
          <>
            <div style={{ marginBottom: '8px' }}>
              <label style={{ display: 'block', fontSize: '12px', marginBottom: '2px' }}>
                Note (0-127):
              </label>
              <input
                type="number"
                min={0}
                max={127}
                value={action.note}
                onChange={(e) => setAction({ ...action, note: parseInt(e.target.value, 10) || 0 })}
                style={inputStyle}
              />
              <span style={{ marginLeft: '8px', fontSize: '12px', color: '#6c757d' }}>
                {getNoteName(action.note)}
              </span>
            </div>
            <div style={{ marginBottom: '8px' }}>
              <label style={{ display: 'block', fontSize: '12px', marginBottom: '2px' }}>
                Channel (0-15):
              </label>
              <input
                type="number"
                min={0}
                max={15}
                value={action.channel}
                onChange={(e) => setAction({ ...action, channel: parseInt(e.target.value, 10) || 0 })}
                style={inputStyle}
              />
            </div>
          </>
        );

      case 'cc':
        return (
          <>
            <div style={{ marginBottom: '8px' }}>
              <label style={{ display: 'block', fontSize: '12px', marginBottom: '2px' }}>
                CC Number (0-127):
              </label>
              <input
                type="number"
                min={0}
                max={127}
                value={action.cc}
                onChange={(e) => setAction({ ...action, cc: parseInt(e.target.value, 10) || 0 })}
                style={inputStyle}
              />
            </div>
            <div style={{ marginBottom: '8px' }}>
              <label style={{ display: 'block', fontSize: '12px', marginBottom: '2px' }}>
                Value (0-127):
              </label>
              <input
                type="number"
                min={0}
                max={127}
                value={action.value}
                onChange={(e) => setAction({ ...action, value: parseInt(e.target.value, 10) || 0 })}
                style={inputStyle}
              />
            </div>
            <div style={{ marginBottom: '8px' }}>
              <label style={{ display: 'block', fontSize: '12px', marginBottom: '2px' }}>
                Channel (0-15):
              </label>
              <input
                type="number"
                min={0}
                max={15}
                value={action.channel}
                onChange={(e) => setAction({ ...action, channel: parseInt(e.target.value, 10) || 0 })}
                style={inputStyle}
              />
            </div>
          </>
        );

      case 'program_change':
        return (
          <>
            <div style={{ marginBottom: '8px' }}>
              <label style={{ display: 'block', fontSize: '12px', marginBottom: '2px' }}>
                Program (0-127):
              </label>
              <input
                type="number"
                min={0}
                max={127}
                value={action.program}
                onChange={(e) => setAction({ ...action, program: parseInt(e.target.value, 10) || 0 })}
                style={inputStyle}
              />
            </div>
            <div style={{ marginBottom: '8px' }}>
              <label style={{ display: 'block', fontSize: '12px', marginBottom: '2px' }}>
                Channel (0-15):
              </label>
              <input
                type="number"
                min={0}
                max={15}
                value={action.channel}
                onChange={(e) => setAction({ ...action, channel: parseInt(e.target.value, 10) || 0 })}
                style={inputStyle}
              />
            </div>
          </>
        );

      case 'chord':
        return (
          <>
            <div style={{ marginBottom: '8px' }}>
              <label style={{ display: 'block', fontSize: '12px', marginBottom: '2px' }}>
                Notes (comma-separated, 0-127):
              </label>
              <input
                type="text"
                value={chordNotesInput}
                onChange={(e) => setChordNotesInput(e.target.value)}
                onBlur={parseChordNotes}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    parseChordNotes();
                  }
                }}
                style={inputStyle}
              />
              <div style={{ marginTop: '4px', fontSize: '11px', color: '#6c757d' }}>
                {action.notes.map((n) => getNoteName(n)).join(', ')}
              </div>
            </div>
            <div style={{ marginBottom: '8px' }}>
              <label style={{ display: 'block', fontSize: '12px', marginBottom: '2px' }}>
                Channel (0-15):
              </label>
              <input
                type="number"
                min={0}
                max={15}
                value={action.channel}
                onChange={(e) => setAction({ ...action, channel: parseInt(e.target.value, 10) || 0 })}
                style={inputStyle}
              />
            </div>
          </>
        );

      case 'layer_toggle':
      case 'layer_momentary':
        return (
          <div style={{ marginBottom: '8px' }}>
            <label style={{ display: 'block', fontSize: '12px', marginBottom: '2px' }}>
              Target Layer (0-15):
            </label>
            <input
              type="number"
              min={0}
              max={15}
              value={action.target_layer}
              onChange={(e) => setAction({ ...action, target_layer: parseInt(e.target.value, 10) || 0 })}
              style={inputStyle}
            />
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <>
      {/* Selected key info */}
      {selectedKey ? (
        <>
          <div
            style={{
              padding: '8px',
              backgroundColor: '#e3f2fd',
              borderRadius: '4px',
              marginBottom: '12px',
            }}
          >
            <div style={{ fontSize: '14px', fontWeight: 'bold' }}>
              Selected Key: ({selectedKey.q}, {selectedKey.r})
            </div>
            {existingOverride && (
              <div style={{ fontSize: '12px', color: '#6c757d', marginTop: '4px' }}>
                Has override: {existingOverride.action.type}
              </div>
            )}
          </div>

          {/* Action type selector */}
          <div style={{ marginBottom: '12px' }}>
            <label style={{ display: 'block', fontSize: '14px', marginBottom: '4px' }}>
              Action Type:
            </label>
            <select
              value={actionType}
              onChange={(e) => handleActionTypeChange(e.target.value as ActionType)}
              style={selectStyle}
            >
              {actionTypes.map((t) => (
                <option key={t.value} value={t.value}>
                  {t.label}
                </option>
              ))}
            </select>
          </div>

          {/* Action-specific editor */}
          {renderActionEditor()}

          {/* Action buttons */}
          <div style={{ display: 'flex', gap: '8px', marginTop: '12px' }}>
            <button onClick={handleSave} style={buttonStyle}>
              {existingOverride ? 'Update' : 'Add'} Override
            </button>
            {existingOverride && (
              <button onClick={handleRemove} style={{ ...buttonStyle, backgroundColor: '#dc3545' }}>
                Remove
              </button>
            )}
            <button onClick={handleClearSelection} style={{ ...buttonStyle, backgroundColor: '#6c757d' }}>
              Clear Selection
            </button>
          </div>
        </>
      ) : (
        <div style={{ color: '#6c757d', fontStyle: 'italic' }}>
          Click a key on the keyboard to select it for override configuration.
        </div>
      )}

      {/* Override count */}
      <div style={{ marginTop: '16px', fontSize: '12px', color: '#6c757d' }}>
        Total overrides: {overrides.length}
      </div>
    </>
  );
}

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '6px',
  fontSize: '14px',
  border: '1px solid #e9ecef',
  borderRadius: '4px',
};

const selectStyle: React.CSSProperties = {
  width: '100%',
  padding: '6px',
  fontSize: '14px',
  border: '1px solid #e9ecef',
  borderRadius: '4px',
};

const buttonStyle: React.CSSProperties = {
  padding: '8px 16px',
  fontSize: '14px',
  color: '#fff',
  backgroundColor: '#007bff',
  border: 'none',
  borderRadius: '4px',
  cursor: 'pointer',
};
